/**
 * `SettingsEndpoint` — the agent-facing surface of a site.
 *
 * 2026-10-23 design pass (founder feedback: "too long and text-heavy"):
 * the ten-row stack of label-on-left / content-on-right rows + the twelve
 * boxed protocol cards had grown to ~8,000 px on desktop and ~12,000 px
 * on phone. The surface is now one card with three visibly-labelled
 * groups:
 *
 *   1. Install snippet + Agent card status (what the merchant acts on).
 *   2. Endpoints (a compact dense list of copyable URLs — one row each
 *      for agent card, catalog, MCP, A2A, PAP — hints behind the `?`
 *      Tooltip atom; the two alternate-discovery blocks grouped below).
 *   3. Commerce (UCP checkout, unchanged).
 *   4. Protocols (compact one-line rows; descriptions still visible but
 *      clamped, same `Read more` expansion contract as before).
 *
 * Nothing is removed: every URL, copy affordance, open link, Shopify
 * rotate warning, publish check, discount editor, Google access key and
 * protocol description is still here. The design review and the before /
 * after screenshots live under `docs/design/settings-install-review-
 * 2026-10-23.md` + the install-design scratchpad.
 *
 * Behaviour pins carried over from the previous ports:
 *
 * - ONE install snippet is rendered, always carrying `data-site-key`
 *   AND `data-proof-token` (founder 2026-06-12 — the token IS the
 *   ownership proof). Until the token loads, the snippet is a
 *   `Loading snippet…` placeholder, never a token-less tag.
 * - Protocol list is READ-ONLY by design — a toggle would overclaim
 *   what the edge actually admits (verified-facts discipline).
 * - The hashed `site_key_hash` form of every URL is accepted at the
 *   edge (ADR-0055), so the card / catalog / MCP / A2A rows stay
 *   viewable after the shown-once raw key leaves memory.
 * - Phase-1 cuts: the Stripe / Shopify connection panel lives in
 *   Settings → Integrations.
 */

import { Check, Copy, Download, ExternalLink, HelpCircle, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import type { CapabilityTier, ProtocolStatus, Site } from '@sill/types';
import { PROTOCOL_REGISTRY, protocolStatusText } from '@sill/types';
import type {
  InstallCoverage,
  SiteRescanCadence,
  SiteRescanSchedule,
} from '@sill/types/api';

import { useApi } from '../../api/api-context.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { getSiteRescanSchedule, putSiteRescanSchedule } from '../../api/site-rescan-schedule.js';
import { API_ORIGIN } from '../../app/env.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useSites, useToast } from '../../app/providers.js';
import { friendlyVerifyMessage } from '../../hooks/useProofCheck.js';
import { useVerifiedSiteKey } from '../../hooks/useVerifiedSiteKey.js';
import { useC } from '../../lib/theme.js';
import {
  buildA2aUrl,
  buildAgentCardOwnDomainUrl,
  buildAgentCardSnippet,
  buildAgentCardUrl,
  buildCatalogOwnDomainUrl,
  buildCatalogUrl,
  buildMcpServerCardOwnDomainUrl,
  buildMcpUrl,
  buildPapOwnDomainUrl,
  buildPapSillUrl,
  buildLlmsTxtUrl,
  buildAgentPageUrl,
  SILL_JWKS_URL,
  buildScriptSnippet,
  downloadDiscoveryJson,
  isA2aSentinelUrl,
  isCatalogSentinelUrl,
  isMcpSentinelUrl,
  isPapSentinelUrl,
} from '../../lib/install-snippet.js';
import { Card, Collapsible, Pill, Tooltip } from '../../components/atoms/index.js';
import { UcpCheckoutRow } from './UcpCheckoutRow.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export interface SettingsEndpointProps {
  site: Site;
  /** Hide the "Re-scan schedule" row. The schedule picker lives on the
   *  Agent Readiness page header now (2026-10-01); when SettingsEndpoint
   *  is rendered inside Settings → Install we drop the row here to keep
   *  one control-in-one-place. */
  hideRescan?: boolean;
}

// ADR-0213 — the protocol list renders from the ONE registry in
// @sill/types. See the file header for the "read-only by design" note.
function protocolTone(status: ProtocolStatus): 'lime' | 'amber' | 'sky' | 'neutral' {
  if (status === 'live') return 'lime';
  if (status === 'built_not_enabled') return 'amber';
  if (status === 'roadmap') return 'sky';
  return 'neutral';
}

export function SettingsEndpoint({ site, hideRescan = false }: SettingsEndpointProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const http = useOptionalHttp();
  const toast = useToast();
  const [scanFreq, setScanFreq] = useState<SiteRescanCadence>('manual');
  const [scanSchedule, setScanSchedule] = useState<SiteRescanSchedule | null>(null);
  const [scanSaving, setScanSaving] = useState(false);

  // Re-scan schedule — persisted per site (site.rescan_cadence) and read
  // by the hourly scheduled-rescan job. No HTTP client (demo mode) keeps
  // the control local.
  useEffect(() => {
    if (http === null) return;
    let live = true;
    void getSiteRescanSchedule(http, site.site_id).then((r) => {
      if (!live || r.kind !== 'ok') return;
      setScanFreq(r.schedule.rescan_cadence);
      setScanSchedule(r.schedule);
    });
    return () => {
      live = false;
    };
  }, [http, site.site_id]);

  const chooseScanFreq = (next: SiteRescanCadence): void => {
    if (next === scanFreq || scanSaving) return;
    const previous = scanFreq;
    setScanFreq(next);
    if (http === null) return;
    setScanSaving(true);
    void putSiteRescanSchedule(http, site.site_id, next).then((r) => {
      setScanSaving(false);
      if (r.kind === 'ok') {
        setScanSchedule(r.schedule);
        return;
      }
      setScanFreq(previous);
      toast({
        tone: 'coral',
        title: r.kind === 'forbidden'
          ? 'Only owners and admins can change the re-scan schedule.'
          : 'Could not save the re-scan schedule. Try again.',
      });
    });
  };

  // Shared row data — fetched once here, threaded into every URL row.
  const verified_key = useVerifiedSiteKey(site);
  const [proofToken, setProofToken] = useState<string | null>(null);
  const [siteKeyHash, setSiteKeyHash] = useState<string | null>(
    typeof site.site_key_hash === 'string' && site.site_key_hash.length > 0
      ? site.site_key_hash
      : null,
  );
  // null = still loading / unknown — treated as not-connected.
  const [shopifyConnected, setShopifyConnected] = useState<boolean | null>(null);
  // ADR-0211 — install coverage drives whether the own-domain mirror
  // of each endpoint actually resolves. `null` while the fetch is in
  // flight; after the fetch resolves, `server_side.state === 'live'`
  // means a Worker or plugin is serving files on the merchant's own
  // domain, so the secondary lines render as live copyable URLs. Any
  // other state renders a muted "not served yet" line that links to
  // the Install step rather than claiming a URL that would 404 or hit
  // the merchant's origin.
  const [installCoverage, setInstallCoverage] = useState<InstallCoverage | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetch(`${API_ORIGIN}/v1/sites/${site.site_id}/proof-token`, {
      credentials: 'include',
    })
      .then((r): Promise<{ proof_token?: string }> =>
        r.ok ? (r.json() as Promise<{ proof_token?: string }>) : Promise.resolve({}),
      )
      .then((d) => {
        if (!cancelled && typeof d.proof_token === 'string') {
          setProofToken(d.proof_token);
        }
      })
      .catch(() => undefined);
    return (): void => {
      cancelled = true;
    };
  }, [site.site_id]);

  useEffect(() => {
    if (siteKeyHash !== null) return undefined;
    let cancelled = false;
    void api.sites
      .get(site.site_id)
      .then((detail) => {
        if (
          !cancelled
          && typeof detail?.site_key_hash === 'string'
          && detail.site_key_hash.length > 0
        ) {
          setSiteKeyHash(detail.site_key_hash);
        }
      })
      .catch(() => undefined);
    return (): void => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, site.site_id]);

  useEffect(() => {
    let cancelled = false;
    void api.sites
      .getConnectorStatus(site.site_id, 'shopify')
      .then((s) => {
        if (!cancelled) {
          setShopifyConnected(s.kind === 'shopify' && s.status === 'connected');
        }
      })
      .catch(() => {
        if (!cancelled) setShopifyConnected(false);
      });
    return (): void => {
      cancelled = true;
    };
  }, [api, site.site_id]);

  useEffect(() => {
    let cancelled = false;
    void api.sites
      .getInstallCoverage(site.site_id)
      .then((c) => {
        if (!cancelled) setInstallCoverage(c);
      })
      .catch(() => {
        // Fetch failure → stay in `null`. The own-domain line renders
        // as "Not served yet" rather than claiming a URL we cannot
        // confirm (never slow a merchant site, never overclaim).
      });
    return (): void => {
      cancelled = true;
    };
  }, [api, site.site_id]);

  // The edge resolves raw-key and hash-form keys identically (ADR-0055);
  // prefer the raw key in-memory, fall back to the hash form so URLs
  // stay viewable across sessions.
  const card_ref = verified_key ?? siteKeyHash;
  const card_url = card_ref !== null ? buildAgentCardUrl(card_ref) : null;
  const card_link = card_ref !== null ? buildAgentCardSnippet(card_ref) : null;
  const catalog_url_raw = card_ref !== null ? buildCatalogUrl(card_ref) : null;
  const catalog_url =
    catalog_url_raw !== null && !isCatalogSentinelUrl(catalog_url_raw) ? catalog_url_raw : null;
  const mcp_url_raw = card_ref !== null ? buildMcpUrl(card_ref) : null;
  const mcp_url =
    mcp_url_raw !== null && !isMcpSentinelUrl(mcp_url_raw) ? mcp_url_raw : null;
  const a2a_url_raw = card_ref !== null ? buildA2aUrl(card_ref) : null;
  const a2a_url =
    a2a_url_raw !== null && !isA2aSentinelUrl(a2a_url_raw) ? a2a_url_raw : null;
  const pap_url_raw = card_ref !== null ? buildPapSillUrl(card_ref) : null;
  const pap_url =
    pap_url_raw !== null && !isPapSentinelUrl(pap_url_raw) ? pap_url_raw : null;
  const llms_url = card_ref !== null ? buildLlmsTxtUrl(card_ref) : null;
  const agent_page_url = card_ref !== null ? buildAgentPageUrl(card_ref) : null;

  // Own-domain mirror URLs — rendered as a muted secondary line under
  // each primary row. The display domain is lowercased so the row
  // reads canonically ("Richardfallah.com" would be surfaced here as
  // "richardfallah.com"). Honours `installCoverage.server_side.state`:
  // live → copyable; anything else → shown muted with an Install link.
  const own_domain = site.domain.toLowerCase();
  const own_domain_live = installCoverage?.server_side.state === 'live';
  const own_domain_source = installCoverage?.server_side.source ?? null;
  const agent_card_own_url = buildAgentCardOwnDomainUrl(own_domain);
  const catalog_own_url = buildCatalogOwnDomainUrl(own_domain);
  const mcp_card_own_url = buildMcpServerCardOwnDomainUrl(own_domain);
  const pap_own_url = buildPapOwnDomainUrl(own_domain);

  // Founder 2026-10-29 — Shopify never allows files under /.well-known
  // on a store's domain, so "Not served yet. Install." there pointed at
  // something no install can fix. On Shopify (unless a Worker runs in
  // front of the store) the own-domain line is the app's /apps/sill/
  // address when the Sill app is connected, and an honest note when it
  // is not.
  const shopify = installCoverage?.platform === 'shopify' && !own_domain_live;
  const app_served = installCoverage?.discovery.app_proxy_card === 'served';
  const SHOPIFY_NO_FILE = "Shopify doesn't allow this file on your store's domain. Agents find it through the link above.";
  const ownLine = (
    base: EndpointOwnDomain,
    app_path: string | null,
  ): EndpointOwnDomain => {
    if (!shopify) return base;
    if (shopifyConnected === true && app_path !== null && base.kind !== 'note') {
      return { ...base, kind: 'mirror', label: 'On your store', url: `https://${own_domain}/apps/sill/${app_path}`, served: app_served };
    }
    return { kind: 'note', text: SHOPIFY_NO_FILE };
  };

  return (
    <Card className="mb-5">
      <SectionHeader title="Endpoint" subtitle="The agent-facing surface of your site." />
      <div className="px-5 sm:px-6">
        <SubSection
          label="Install snippet"
          hint="Paste once into every page. The proof token verifies you own the site."
        >
          <InstallSnippetRow
            site={site}
            verifiedKey={verified_key}
            siteKeyHash={siteKeyHash}
            proofToken={proofToken}
            shopifyConnected={shopifyConnected}
          />
        </SubSection>

        <SubSection
          label="Agent card"
          hint="Your signed capability card, served by Sill."
        >
          <AgentCardStatus
            site={site}
            card_ref={card_ref}
            shopifyConnected={shopifyConnected}
          />
        </SubSection>

        <KickerHeading
          label="Endpoints"
          hint="One URL per protocol. Copy and paste where the agent client expects it."
        />
        <EndpointListRow
          label="Agent card URL"
          url={card_url}
          hint="Your signed capability card at the edge. Agents fetch this to see your name, your skills, and the endpoints they can call."
          openHref={card_url}
          openAriaLabel="Open agent card"
          copyAriaLabel="Copy agent card URL"
          ownDomain={ownLine({
            kind: 'mirror',
            label: 'On your domain',
            url: agent_card_own_url,
            served: own_domain_live,
            source: own_domain_source,
            copyAriaLabel: 'Copy own-domain agent card URL',
          }, 'agent-card.json')}
        />
        <EndpointListRow
          label="Catalog URL"
          url={catalog_url}
          hint="Your signed ARD catalog, served by Sill at the edge. Agents fetch this for product details and availability."
          openHref={catalog_url}
          openLabel="View catalog"
          openAriaLabel="View catalog"
          copyAriaLabel="Copy catalog URL"
          ownDomain={ownLine({
            kind: 'mirror',
            label: 'On your domain',
            url: catalog_own_url,
            served: own_domain_live,
            source: own_domain_source,
            copyAriaLabel: 'Copy own-domain catalog URL',
          }, 'ai-catalog.json')}
        />
        <EndpointListRow
          label="MCP endpoint"
          url={mcp_url}
          hint="Your MCP (Model Context Protocol) server, for agent skill calls. POST endpoint — paste into your MCP client config."
          copyAriaLabel="Copy MCP endpoint URL"
          ownDomain={ownLine({
            // The MCP endpoint has no own-domain equivalent. The
            // Worker/plugin serve a discovery pointer at
            // `/.well-known/mcp/server-card.json` whose `endpoint`
            // field is the Sill URL above; label it as a pointer so
            // the row never implies the endpoint moved to the
            // merchant's origin.
            kind: 'pointer',
            label: 'Discovery pointer',
            url: mcp_card_own_url,
            served: own_domain_live,
            source: own_domain_source,
            copyAriaLabel: 'Copy own-domain MCP discovery pointer',
          }, null)}
          caption={
            // ADR-0261 — the per-site MCP endpoint above is for AGENTS
            // shopping this site. A merchant connecting their OWN
            // Claude / ChatGPT to Sill uses a different server, bound
            // to the user not the site. Point them at it so the two
            // never get confused.
            <>
              This is the MCP server agents use to shop your site. To operate
              Sill from your own assistant, see{' '}
              <a
                href="?view=account&account_tab=connected_apps"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                Account settings → Connected apps
              </a>
              .
            </>
          }
        />
        <EndpointListRow
          label="A2A messaging"
          url={a2a_url}
          hint="Where agents send an A2A message naming one of your skills, read the running task, or cancel it. JSON-RPC endpoint, listed on your agent card."
          copyAriaLabel="Copy A2A endpoint URL"
          ownDomain={{
            // A2A has no own-domain mirror and no pointer file. The
            // endpoint is reached via the agent card the row above
            // publishes; the honest line here says so.
            kind: 'note',
            text: 'No own-domain path. Agents reach this via your agent card.',
          }}
        />
        <EndpointListRow
          label="Personal Agent Protocol"
          url={pap_url}
          hint="The file a shopper's personal agent fetches to open a guest session with your business. Guest sessions only — no shopper sign-in, no payments."
          openHref={pap_url}
          openAriaLabel="Open PAP discovery file"
          copyAriaLabel="Copy PAP discovery URL"
          ownDomain={ownLine({
            kind: 'mirror',
            label: 'On your domain',
            url: pap_own_url,
            served: own_domain_live,
            source: own_domain_source,
            copyAriaLabel: 'Copy own-domain PAP discovery URL',
          }, null)}
        />
        <EndpointListRow
          label="AP2 payments"
          url={a2a_url}
          hint="AP2 (Agent Payments Protocol) runs over A2A messaging: the shopper's agent sends its signed intent and payment mandates to the A2A endpoint above. Inside UCP checkout, AP2 runs as the mandates extension."
          copyAriaLabel="Copy AP2 endpoint URL"
          ownDomain={{
            kind: 'note',
            text: 'Same URL as A2A messaging. AP2 has no separate endpoint.',
          }}
        />
        <EndpointListRow
          label="llms.txt"
          url={llms_url}
          hint="A plain-text summary of your site and its agent endpoints, generated by Sill from your skills."
          openHref={llms_url}
          openAriaLabel="Open llms.txt"
          copyAriaLabel="Copy llms.txt URL"
          ownDomain={{
            // Not a mirror: a site may serve its own /llms.txt (an SEO
            // plugin, say), and agents read that one at the root. On
            // Shopify it is always Shopify's own file.
            kind: 'note',
            text: installCoverage?.platform === 'shopify'
              ? "Your store's llms.txt is written by Shopify. Sill's version is linked from your agent card and agent page."
              : `If ${own_domain}/llms.txt is your own file, agents read that one first. Sill's version is linked from your agent card and agent page.`,
          }}
        />
        <EndpointListRow
          label="Agent page"
          url={agent_page_url}
          hint="An HTML page for agents that read web pages rather than JSON. It links every endpoint on this list."
          openHref={agent_page_url}
          openAriaLabel="Open agent page"
          copyAriaLabel="Copy agent page URL"
          ownDomain={ownLine({
            kind: 'mirror',
            label: 'On your domain',
            url: `https://${own_domain}/agent`,
            served: own_domain_live,
            source: own_domain_source,
            copyAriaLabel: 'Copy own-domain agent page URL',
          }, 'agent')}
        />
        <EndpointListRow
          label="Signing keys (JWKS)"
          url={SILL_JWKS_URL}
          hint="Sill's public keys. Anyone can use them to check the signatures on your agent card, catalog and records."
          openHref={SILL_JWKS_URL}
          openAriaLabel="Open JWKS"
          copyAriaLabel="Copy JWKS URL"
          ownDomain={{
            kind: 'note',
            text: 'Same for every site. Web Bot Auth has no URL here: Sill checks the signatures agents send, using the agent\u2019s own published keys.',
          }}
        />
        <AlternateDiscoverySection
          site={site}
          card_ref={card_ref}
          card_link={card_link}
        />

        <KickerHeading label="Commerce" />
        {installCoverage?.platform === 'shopify' ? (
          // Founder 2026-10-29 — every Shopify store already publishes its
          // own UCP profile and checkout at /.well-known/ucp. Sill does not
          // replace it, so its own UCP endpoint is not offered here.
          <SubSection
            label="UCP checkout"
            hint="Universal Commerce Protocol checkout for shopping agents."
          >
            <p data-testid="ucp-shopify-note" style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, lineHeight: 1.6, margin: 0 }}>
              Your store's UCP checkout is run by Shopify at {own_domain}/.well-known/ucp. Sill leaves it untouched.
            </p>
          </SubSection>
        ) : (
          <SubSection
            label="UCP checkout"
            hint="Your store's Universal Commerce Protocol endpoint: shopping platforms that speak UCP can check out through it, governed by your guardrails."
          >
            <UcpCheckoutRow site_id={site.site_id} />
          </SubSection>
        )}

        <KickerHeading
          label="Protocols"
          hint="Protocols Sill speaks at the agent boundary."
        />
        <div
          id="protocol-support"
          data-testid="protocol-support-list"
          className="py-1"
          style={{ borderBottom: hideRescan ? 'none' : `1px solid ${C.borderSoft}` }}
        >
          {PROTOCOL_REGISTRY.map((p) => (
            <ProtocolRow key={p.id} p={p} />
          ))}
        </div>

        {hideRescan ? null : (
          <SubSection
            label="Re-scan schedule"
            hint="How often Sill re-scans your site to refresh the scan results and readiness checks. Manual scans only when you click Rescan."
            last
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-sm">
              {(['daily', 'weekly', 'monthly', 'manual'] as const).map((f) => (
                <button
                  type="button"
                  key={f}
                  aria-pressed={scanFreq === f}
                  disabled={scanSaving}
                  onClick={() => chooseScanFreq(f)}
                  className="px-3 py-2"
                  style={{
                    background: scanFreq === f ? C.surface2 : C.surface,
                    border: `1px solid ${scanFreq === f ? C.lime : C.border}`,
                    color: C.text,
                    fontFamily: sansFont,
                    fontSize: 12,
                    borderRadius: 2,
                    textTransform: 'capitalize',
                  }}
                >
                  {f}
                </button>
              ))}
            </div>
            {scanSchedule !== null ? (
              <div style={{ fontFamily: sansFont, fontSize: 11.5, color: C.muted, marginTop: 8 }}>
                {scanSchedule.last_scan_at !== null
                  ? `Last scan ${new Date(scanSchedule.last_scan_at).toLocaleDateString()}. `
                  : 'No scan yet. '}
                {scanSchedule.next_scan_at !== null
                  ? `Next scheduled scan ${Date.parse(scanSchedule.next_scan_at) <= Date.now() ? 'within the hour' : new Date(scanSchedule.next_scan_at).toLocaleDateString()}.`
                  : 'No scheduled scans.'}
              </div>
            ) : null}
          </SubSection>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------
// Shared layout primitives for this surface.
// ---------------------------------------------------------------------

/**
 * The card header. Mirrors the shape used by `SettingsInstall`'s outer
 * sections so the three Install-tab cards read as siblings.
 */
function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }): JSX.Element {
  const C = useC();
  return (
    <div
      style={{
        padding: '20px 24px 16px',
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <h2
        style={{
          margin: 0,
          fontFamily: serifFont,
          fontSize: 22,
          color: C.text,
          letterSpacing: -0.5,
          fontWeight: 400,
        }}
      >
        {title}
      </h2>
      {subtitle ? (
        <div
          style={{
            marginTop: 4,
            fontFamily: sansFont,
            fontSize: 13,
            color: C.muted,
            lineHeight: 1.5,
          }}
        >
          {subtitle}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A sub-section inside the Endpoint card. Label (+ optional `?` tooltip
 * for the hint) on top, content below, hairline bottom divider.
 *
 * Replaces the three-column `Row` with its hint-column: on desktop the
 * old layout gave the hint 1/3 of the row width even when the hint
 * repeated what the content already showed. Here the hint lives in a
 * Tooltip and the content gets the full width.
 */
function SubSection({
  label,
  hint,
  children,
  last = false,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  last?: boolean;
}): JSX.Element {
  const C = useC();
  return (
    <div
      className="py-4"
      style={last ? undefined : { borderBottom: `1px solid ${C.borderSoft}` }}
    >
      <LabelWithHint label={label} hint={hint} />
      <div className="mt-2">{children}</div>
    </div>
  );
}

/**
 * A dense-list header that groups a stack of related rows (Endpoints,
 * Commerce, Protocols). Mono uppercase kicker + optional one-line hint.
 * No `borderTop` — the preceding `SubSection` already draws its own
 * hairline bottom-border, so adding one here renders a double divider.
 */
function KickerHeading({ label, hint }: { label: string; hint?: string }): JSX.Element {
  const C = useC();
  return (
    <div className="pt-4 pb-1.5">
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 2,
          color: C.muted,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      {hint ? (
        <div
          style={{
            marginTop: 2,
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            lineHeight: 1.5,
            maxWidth: 640,
          }}
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
}

/** Label text + a `?` tooltip trigger when a hint is provided. */
function LabelWithHint({ label, hint }: { label: string; hint?: string | undefined }): JSX.Element {
  const C = useC();
  return (
    <div className="flex items-center gap-1.5">
      <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>
        {label}
      </span>
      {hint ? (
        <Tooltip content={hint} side="top" aria-label={`About ${label}`}>
          <span
            aria-hidden
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: C.muted,
            }}
          >
            <HelpCircle size={13} strokeWidth={1.5} />
          </span>
        </Tooltip>
      ) : null}
    </div>
  );
}

/**
 * The own-domain mirror of an endpoint. Shapes:
 *
 *   - `mirror`  the Worker/plugin serves this path and proxies back to
 *               Sill. Renders a secondary row with a small kicker
 *               label, mono URL, and an independent copy button. When
 *               the install is not live the URL is shown muted with a
 *               "Not served yet" note that links to the install step
 *               (never claim an own-domain URL that isn't served).
 *   - `pointer` same shape as `mirror`, but the kicker reads
 *               "Discovery pointer" instead of "On your domain" so
 *               rows like MCP (whose own-domain file is a pointer, not
 *               the endpoint itself) stay honest.
 *   - `note`    plain text — used when no own-domain equivalent exists
 *               at all (A2A messaging). No URL, no copy.
 */
type EndpointOwnDomain =
  | {
      readonly kind: 'mirror' | 'pointer';
      readonly label: string;
      readonly url: string;
      /** True when the Worker/plugin install is live. False or
       *  undefined → the row is muted and non-copyable. */
      readonly served: boolean;
      /** 'worker' | 'plugin' | null — carried through for test
       *  assertions; UI reads the same copy regardless. */
      readonly source: 'worker' | 'plugin' | null;
      readonly copyAriaLabel: string;
    }
  | { readonly kind: 'note'; readonly text: string };

/**
 * One compact row in the ENDPOINTS list.
 *
 * Shape:
 *
 *   label                       primary (Sill) URL           [copy] [open?]
 *                               On your domain → own URL     [copy]
 *
 * The primary (Sill) URL is the authoritative, always-live source of
 * truth — signed, directly served by the edge, independent of any
 * install the merchant has in place. The secondary own-domain line
 * mirrors it for agents that discover the merchant via a `.well-known/`
 * fetch; it is a copyable artifact but ALWAYS gated on install
 * coverage. When the install is not live the mirror is shown muted
 * with a "Not served yet" note that links to the install step rather
 * than claiming a URL that would 404 at the merchant's origin.
 *
 * Operational notes like "POST endpoint" live inside the `?` tooltip
 * so the primary row stays one line tall. The row stacks to
 * label-above-URL on mobile.
 */
function EndpointListRow({
  label,
  url,
  hint,
  openHref,
  openLabel,
  openAriaLabel,
  copyAriaLabel,
  ownDomain,
  caption,
}: {
  label: string;
  url: string | null;
  hint?: string;
  openHref?: string | null;
  /** When set, the open button renders with this text + icon. Omitting
   *  it (the common case) renders an icon-only button. */
  openLabel?: string;
  openAriaLabel?: string;
  copyAriaLabel: string;
  ownDomain?: EndpointOwnDomain;
  /** Optional one-line explainer rendered below the row (ADR-0261 §5
   *  pointer caption on the MCP endpoint row). */
  caption?: ReactNode;
}): JSX.Element {
  const C = useC();
  const [copied, setCopied] = useState(false);
  const copy = (): void => {
    if (url === null) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(url);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-x-4 gap-y-1 py-2"
      style={{ borderBottom: `1px solid ${C.borderSoft}` }}
    >
      <div className="sm:self-start sm:pt-1.5">
        <LabelWithHint label={label} hint={hint} />
      </div>
      <div className="min-w-0 self-center flex flex-col gap-1.5">
        {url === null ? (
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              lineHeight: 1.5,
            }}
          >
            Verify domain first.
          </div>
        ) : (
          <div
            className="px-2.5 py-1.5 flex items-center gap-2"
            style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}
          >
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 12,
                color: C.text,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
                whiteSpace: 'nowrap',
              }}
            >
              {url}
            </span>
            <button
              type="button"
              aria-label={copyAriaLabel}
              onClick={copy}
              className="flex-shrink-0 p-1"
              style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2 }}
            >
              {copied ? <Check size={11} color={C.limeFg} /> : <Copy size={11} color={C.muted} />}
            </button>
            {typeof openHref === 'string' && openHref.length > 0 ? (
              openLabel !== undefined ? (
                <a
                  href={openHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={openAriaLabel ?? openLabel}
                  className="flex-shrink-0 flex items-center gap-1 px-1.5 py-1"
                  style={{
                    background: C.bg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                    fontFamily: sansFont,
                    fontSize: 11,
                    color: C.muted,
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <ExternalLink size={11} color={C.muted} />
                  {openLabel}
                </a>
              ) : (
                <a
                  href={openHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={openAriaLabel ?? `Open ${label}`}
                  className="flex-shrink-0 p-1"
                  style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2 }}
                >
                  <ExternalLink size={11} color={C.muted} />
                </a>
              )
            ) : null}
          </div>
        )}
        {ownDomain !== undefined ? (
          <OwnDomainLine ownDomain={ownDomain} />
        ) : null}
        {caption !== undefined ? (
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 11.5,
              color: C.muted,
              lineHeight: 1.5,
              paddingLeft: 2,
            }}
            data-testid="endpoint-row-caption"
          >
            {caption}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The secondary line under an EndpointListRow. See `EndpointOwnDomain`
 * for the three shapes. Written as a separate component so the
 * conditional branches stay readable and so the primary row's render
 * tree does not expand with the mirror/pointer/note states.
 */
function OwnDomainLine({ ownDomain }: { ownDomain: EndpointOwnDomain }): JSX.Element {
  const C = useC();
  const [copied, setCopied] = useState(false);
  if (ownDomain.kind === 'note') {
    return (
      <div
        className="flex items-center gap-1.5 pl-2.5"
        style={{ fontFamily: sansFont, fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}
      >
        <Arrow />
        <span>{ownDomain.text}</span>
      </div>
    );
  }
  const served = ownDomain.served;
  const copy = (): void => {
    if (!served) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(ownDomain.url);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  const kicker_color = served ? C.muted : C.muted;
  const url_color = served ? C.text : C.muted;
  return (
    <div
      className="flex flex-wrap items-center gap-x-2 gap-y-0.5 pl-2.5"
      data-testid={`own-domain-${ownDomain.kind}`}
      data-served={served ? 'true' : 'false'}
    >
      <Arrow />
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          letterSpacing: 1.4,
          color: kicker_color,
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {ownDomain.label}
      </span>
      <span
        className="min-w-0"
        style={{
          fontFamily: monoFont,
          fontSize: 11.5,
          color: url_color,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
          minWidth: 0,
          opacity: served ? 1 : 0.75,
        }}
      >
        {ownDomain.url}
      </span>
      {served ? (
        <button
          type="button"
          onClick={copy}
          aria-label={ownDomain.copyAriaLabel}
          className="flex-shrink-0 p-1"
          style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2 }}
        >
          {copied ? <Check size={10} color={C.limeFg} /> : <Copy size={10} color={C.muted} />}
        </button>
      ) : (
        <span
          style={{
            fontFamily: sansFont,
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.5,
            whiteSpace: 'nowrap',
          }}
        >
          Not served yet.{' '}
          <a
            href="#install"
            style={{
              color: C.text,
              textDecoration: 'underline',
              textDecorationColor: C.border,
            }}
          >
            Install
          </a>
          .
        </span>
      )}
    </div>
  );
}

/** The arrow glyph that prefixes an own-domain secondary line. Rendered
 *  inside a small mono character so it reads as a return/sub-item mark
 *  consistent with the rest of the row's density. */
function Arrow(): JSX.Element {
  const C = useC();
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 14,
        height: 14,
        color: C.muted,
        fontFamily: monoFont,
        fontSize: 12,
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      {'↳'}
    </span>
  );
}

// ---------------------------------------------------------------------
// Agent card — status + Publish + the two alternate-discovery blocks
// (HTML link tag, well-known download).
// ---------------------------------------------------------------------

/**
 * The status half of the Agent card section — the pill, the one-line
 * explanation, and (when not published) the Publish button. The URL
 * lives in the ENDPOINTS list, the link tag + well-known download sit
 * underneath in `AlternateDiscoverySection`.
 */
function AgentCardStatus({
  site,
  card_ref,
  shopifyConnected,
}: {
  site: Site;
  card_ref: string | null;
  shopifyConnected: boolean | null;
}): JSX.Element {
  const C = useC();
  const api = useApi();
  const toast = useToast();
  const [tier, setTier] = useState<CapabilityTier | null>(null);
  const [tierNonce, setTierNonce] = useState(0);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api.sites
      .getStripeRail(site.site_id)
      .then((s) => {
        if (!cancelled) setTier(s.capability_tier);
      })
      .catch(() => {
        if (!cancelled) setTier(null);
      });
    return (): void => {
      cancelled = true;
    };
  }, [api, site.site_id, tierNonce]);

  const publish = async (): Promise<void> => {
    if (publishing) return;
    setPublishing(true);
    try {
      const resp = await fetch(
        `${API_ORIGIN}/v1/sites/${site.site_id}/proof-check`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({}),
        },
      );
      let payload: { ok?: boolean; reason?: string; error?: string } = {};
      try {
        payload = (await resp.json()) as typeof payload;
      } catch {
        /* non-JSON body — fall through to status-based mapping */
      }
      if (resp.ok && payload.ok === true) {
        toast({
          title: 'Homepage proof recorded',
          body: 'Re-checking publication status…',
          tone: 'lime',
        });
        setTierNonce((n) => n + 1);
      } else {
        const m = friendlyVerifyMessage(
          resp.status,
          payload.reason ?? payload.error,
          site.domain,
        );
        toast({ title: m.title, body: m.body, tone: m.tone });
      }
    } catch (err) {
      toast({
        title: 'Could not run the publish check',
        body: err instanceof Error ? err.message : 'Unknown error',
        tone: 'coral',
      });
    } finally {
      setPublishing(false);
    }
  };

  // Tiered publication (founder decision 2026-06-12): the card serves
  // once ownership is proven (T1+). T1 gets a discovery card —
  // transactional skills appear at T2+ (domain-matched OAuth).
  const published = tier === 'T1' || tier === 'T2' || tier === 'T3';
  const transactional_listed = tier === 'T2' || tier === 'T3';

  if (card_ref === null) {
    return (
      <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
        Loading card status…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        {published ? (
          <>
            <Pill tone="lime">PUBLISHED</Pill>
            <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
              {transactional_listed
                ? 'Verified agents can fetch your card.'
                : 'Discovery card live. Transactional skills are listed once a store or payment rail is connected.'}
            </span>
          </>
        ) : (
          <>
            <Pill tone="amber">NOT PUBLISHED</Pill>
            <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>
              {tier === null ? 'TIER —' : `TIER ${tier.replace('legacy_', '')}`}
            </span>
          </>
        )}
      </div>

      {!published ? (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => void publish()}
            disabled={publishing}
            className="flex items-center gap-2 px-3 py-2"
            style={{
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.text,
              fontFamily: sansFont,
              fontSize: 12,
              fontWeight: 500,
              borderRadius: 2,
              opacity: publishing ? 0.6 : 1,
            }}
          >
            {publishing ? (
              <>
                <RefreshCw size={12} style={{ animation: 'spin 1.4s linear infinite' }} />
                Checking homepage…
              </>
            ) : (
              'Publish agent card'
            )}
          </button>
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
            Requires the install snippet on your homepage. Transactional
            skills appear on the card once Shopify or Stripe is connected.
            {shopifyConnected === true ? (
              <>
                {' '}Shopify&apos;s storefront tag doesn&apos;t carry the
                proof token, so paste the install snippet into your theme
                first. You can remove it after publishing — the proof is
                permanent.
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The two optional ways agents can discover the card — the HTML `<link>`
 * tag merchants paste into `<head>`, and the static `.well-known`
 * download for crawlers that fetch the file directly. Grouped in the
 * ENDPOINTS section (right after the five URL rows) because they're
 * properties of the same artifact — the agent card.
 *
 * Collapsed by default (founder 2026-10-23 round 2: the merchant uses
 * these once at install time, then rarely again). The `inline` variant
 * is a muted text trigger so the collapsed state reads as "a link to
 * open", not "a disabled section".
 */
function AlternateDiscoverySection({
  site,
  card_ref,
  card_link,
}: {
  site: Site;
  card_ref: string | null;
  card_link: string | null;
}): JSX.Element | null {
  const C = useC();
  const [downloading, setDownloading] = useState(false);
  if (card_ref === null || card_link === null) return null;
  return (
    <div className="py-3" style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
      <Collapsible label="Alternate ways agents find your card" variant="inline">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2">
          <div className="space-y-2 min-w-0">
            <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, fontWeight: 500 }}>
              HTML link tag
            </div>
            <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
              Paste in your <span style={{ fontFamily: monoFont }}>&lt;head&gt;</span> so
              agents that parse HTML can find your card without a separate discovery file.
            </div>
            <SnippetBlock text={card_link} copyLabel="Copy card link tag" />
          </div>
          <div className="space-y-2 min-w-0">
            <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, fontWeight: 500 }}>
              Well-known discovery file
            </div>
            <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
              Some agents fetch{' '}
              <span style={{ fontFamily: monoFont }}>/.well-known/agent-card.json</span>{' '}
              directly. Upload this file once — it points permanently to your live card,
              so no re-upload is needed when skills change.
            </div>
            <button
              type="button"
              disabled={downloading}
              onClick={() => {
                if (downloading) return;
                setDownloading(true);
                downloadDiscoveryJson({
                  name: site.name.length > 0 ? site.name : site.domain,
                  url: `https://${site.domain}`,
                  site_key: card_ref,
                });
                setDownloading(false);
              }}
              className="flex items-center gap-2 px-3 py-1.5"
              style={{
                background: 'transparent',
                border: `1px solid ${C.border}`,
                color: C.muted,
                fontFamily: sansFont,
                fontSize: 12,
                borderRadius: 2,
                cursor: downloading ? 'default' : 'pointer',
                opacity: downloading ? 0.6 : 1,
              }}
            >
              <Download size={12} />
              {downloading ? 'Preparing…' : 'Download agent-card.json'}
            </button>
          </div>
        </div>
      </Collapsible>
    </div>
  );
}

// ---------------------------------------------------------------------
// Install snippet — the one `<script>` tag + the rotate-key flow.
// Behaviour unchanged from the previous port — just unwrapped from the
// three-column Row to sit flush under its SubSection label.
// ---------------------------------------------------------------------

/** Shared copy-to-clipboard pre block with a COPY button.
 *
 *  Long attribute values in the install snippet (`data-site-key="…"`,
 *  `data-proof-token="…"`) are single tokens with no whitespace break
 *  points, so a hard-wrapping pre splits them mid-token. Horizontal
 *  scroll (founder 2026-10-23 round 2) keeps every attribute on one
 *  line and the merchant swipes to read — the COPY button always
 *  grabs the full value regardless of what is visible. */
function SnippetBlock({
  text,
  copyLabel,
}: {
  text: string;
  copyLabel: string;
}): JSX.Element {
  const C = useC();
  const [copied, setCopied] = useState(false);
  const copy = (): void => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(text);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div
      className="relative"
      style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}
    >
      <button
        type="button"
        onClick={copy}
        aria-label={copyLabel}
        className="absolute top-2.5 right-2.5 flex items-center gap-1.5 px-2 py-1 z-10"
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          fontFamily: monoFont,
          fontSize: 10,
          color: copied ? C.limeFg : C.muted,
          borderRadius: 2,
        }}
      >
        {copied ? <Check size={10} /> : <Copy size={10} />}
        {copied ? 'COPIED' : 'COPY'}
      </button>
      <pre
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.text,
          lineHeight: 1.7,
          whiteSpace: 'pre',
          overflowX: 'auto',
          padding: '12px',
          paddingRight: 72,
          margin: 0,
        }}
      >
        {text}
      </pre>
    </div>
  );
}

/**
 * The canonical install snippet + the rotate-key action. One snippet,
 * always token-carrying: the same tag records traffic AND serves as
 * the ownership proof the publish check looks for.
 *
 * Two states:
 *   - key verified in memory → snippet + guarded "Rotate key".
 *   - key hidden (shown-once contract, or the cached key failed the
 *     hash check after an out-of-band rotation) → the only recovery is
 *     minting a fresh key.
 */
function InstallSnippetRow({
  site,
  verifiedKey,
  siteKeyHash,
  proofToken,
  shopifyConnected,
}: {
  site: Site;
  verifiedKey: string | null;
  siteKeyHash: string | null;
  proofToken: string | null;
  shopifyConnected: boolean | null;
}): JSX.Element {
  const C = useC();
  const api = useApi();
  const auth = useOptionalAuth();
  const toast = useToast();
  const { setActiveSite, updateSite } = useSites();
  const [rotating, setRotating] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // The rotate-key route is owner/admin-only server-side. The dashboard
  // hides the affordance for reviewer/viewer as UX; the server 403s
  // regardless — this is defense-in-depth, not the security boundary.
  // Legacy test scaffolds that mount without `<AuthProvider>` get a
  // null `auth` and fall through to the owner path (parity with the
  // pre-existing read-only hint pattern — the server is authoritative).
  const role =
    auth !== null && auth.state.kind === 'authenticated'
      ? auth.state.user.role
      : null;
  const canRotate = role === null || role === 'owner' || role === 'admin';

  // The site_key is a PUBLIC identifier — it ships in the page's `<script
  // data-site-key>` tag, visible in view-source. The edge accepts the
  // site_key_hash as an interchangeable ingest identifier (ADR-0055) so
  // we always have a usable site_ref once either side's fetch resolves.
  const site_ref = verifiedKey ?? siteKeyHash;
  // NEVER render a token-less snippet: the data-proof-token is the
  // ownership-proof half of the install (founder requirement, 2026-06-12).
  const snippet =
    site_ref !== null && proofToken !== null
      ? buildScriptSnippet(site_ref, proofToken)
      : null;

  const rotate = async (): Promise<void> => {
    if (rotating) return;
    setRotating(true);
    try {
      const rotated = await api.sites.rotateKey(site.site_id);
      updateSite(rotated.site_id, rotated);
      setActiveSite(rotated);
      setConfirming(false);
      if (shopifyConnected === true) {
        // ADR-0053/0055 — the Shopify storefront serves the embed via a
        // ScriptTag whose `?site=` value is the OLD key hash. Re-run the
        // same install the OAuth callback ran; the server replaces the
        // existing tag in place.
        try {
          const r = await api.sites.retryShopifyEmbedInstall(site.site_id);
          if (r.embed_install_status === 'installed') {
            toast({
              title: 'New key generated',
              body: 'Storefront tag updated automatically. Replace any manually pasted snippets.',
              tone: 'lime',
            });
          } else {
            toast({
              title: 'New key generated — storefront tag not updated',
              body: `Shopify re-install returned “${r.embed_install_status}”. The storefront still serves the old key — use “Retry install” on the site card or reconnect Shopify.`,
              tone: 'amber',
            });
          }
        } catch (err) {
          toast({
            title: 'New key generated — storefront tag not updated',
            body: `${err instanceof Error ? err.message : 'Unknown error'} — the storefront still serves the old key. Use “Retry install” on the site card or reconnect Shopify.`,
            tone: 'amber',
          });
        }
      } else {
        toast({
          title: 'New key generated',
          body: 'The old key is now invalid. Replace the snippet everywhere it was deployed.',
          tone: 'lime',
        });
      }
    } catch (err) {
      toast({
        title: 'Could not generate key',
        body: err instanceof Error ? err.message : 'Unknown error',
        tone: 'coral',
      });
    } finally {
      setRotating(false);
    }
  };

  return (
    <div className="space-y-3">
      {snippet !== null ? (
        <SnippetBlock text={snippet} copyLabel="Copy install snippet" />
      ) : (
        <div
          className="p-3"
          style={{
            background: C.surface2,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
          }}
        >
          Loading snippet…
        </div>
      )}

      {!canRotate ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.5,
          }}
        >
          Only owners and admins can rotate the site key.
        </div>
      ) : confirming ? (
        <div
          className="p-3"
          style={{
            background: C.coralDim,
            border: `1px solid rgba(255,107,71,0.3)`,
            borderRadius: 3,
          }}
        >
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.text,
              lineHeight: 1.5,
              marginBottom: 10,
            }}
          >
            {shopifyConnected === true ? (
              <>
                Rotating invalidates the current key. Your Shopify
                storefront tag updates <strong>automatically</strong>;
                any manually pasted copies stop working until replaced.
                Continue?
              </>
            ) : (
              <>
                Rotating invalidates the key
                <strong> currently deployed on your site</strong>.
                Traffic stops recording until you paste the new snippet.
                Continue?
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void rotate()}
              disabled={rotating}
              className="flex items-center gap-2 px-3 py-2"
              style={{
                background: C.coral,
                color: C.ink,
                fontFamily: sansFont,
                fontWeight: 600,
                fontSize: 12,
                borderRadius: 2,
                opacity: rotating ? 0.6 : 1,
              }}
            >
              {rotating ? (
                <>
                  <RefreshCw size={12} style={{ animation: 'spin 1.4s linear infinite' }} />
                  Rotating…
                </>
              ) : (
                'Rotate key'
              )}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={rotating}
              className="px-3 py-2"
              style={{
                background: 'transparent',
                border: `1px solid ${C.border}`,
                color: C.muted,
                fontFamily: sansFont,
                fontSize: 12,
                borderRadius: 2,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="flex items-center gap-2 px-3 py-2"
          style={{
            background: 'transparent',
            border: `1px solid ${C.border}`,
            color: C.text,
            fontFamily: sansFont,
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 2,
          }}
        >
          <RefreshCw size={12} color={C.muted} />
          Rotate key
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Protocol row — compact one-line format.
// ---------------------------------------------------------------------

/**
 * A status chip for the Protocols list. Matches the Pill atom's six-tone
 * palette, but allows the text to word-wrap on narrow mobile widths —
 * the shared Pill is `whiteSpace: nowrap` by design (every other use is
 * a short word) and would otherwise shrink to one character per line
 * when a long bound ("LIVE · Worker and plugin installs") exceeds the
 * row width.
 */
function ProtocolStatusChip({
  tone,
  children,
}: {
  tone: 'lime' | 'amber' | 'sky' | 'neutral';
  children: ReactNode;
}): JSX.Element {
  const C = useC();
  const tones: Record<'lime' | 'amber' | 'sky' | 'neutral', { bg: string; fg: string; bd: string }> = {
    neutral: { bg: C.surface2, fg: C.muted, bd: C.border },
    lime: { bg: C.limeDim, fg: C.limeFg, bd: 'rgba(217,255,63,0.3)' },
    amber: { bg: C.amberDim, fg: C.amberFg, bd: 'rgba(255,182,39,0.3)' },
    sky: { bg: C.skyDim, fg: C.skyFg, bd: 'rgba(127,184,255,0.3)' },
  };
  const t = tones[tone];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 6px',
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.bd}`,
        borderRadius: 2,
        fontFamily: monoFont,
        fontSize: 9,
        letterSpacing: 0.8,
        fontWeight: 500,
        lineHeight: 1.4,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        maxWidth: '100%',
      }}
    >
      {children}
    </span>
  );
}

/**
 * One protocol row — ID chip + name + vendor + description (clamped) +
 * status pill. The former per-row bordered Card is gone; the whole list
 * reads as a dense status table (12 rows × ~44 px, previously 12 rows ×
 * ~180 px). Click-through on Read more still expands the description
 * inline for the handful of registry entries that spill past one line.
 *
 * Mobile: id chip + name on the first line, status pill right-aligned,
 * description full-width underneath (so narrow screens don't squeeze the
 * copy to a few characters per line).
 */
/**
 * One protocol row — a single dense table line on desktop: ID chip ·
 * name + muted vendor · right-aligned status chip. The full description
 * lives inside the `?` Tooltip on the name so the row stays ~38 px
 * tall. On mobile the row stacks: ID + name on the first line, status
 * on its own line underneath (so long bounds like
 * "LIVE · Worker and plugin installs" don't fight the name for width).
 *
 * The hidden `protocol-desc-${id}` span is kept so tooling can still
 * assert on description content (search, docs harness) — it is
 * screen-reader-visible via `aria-describedby` on the trigger.
 */
function ProtocolRow({ p }: { p: (typeof PROTOCOL_REGISTRY)[number] }): JSX.Element {
  const C = useC();
  const idText = p.id.toUpperCase().replace(/_/g, ' ');
  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
      style={{ borderBottom: `1px solid ${C.borderSoft}`, minHeight: 36 }}
      data-testid={`protocol-row-${p.id}`}
      data-status={p.status}
    >
      {/* ID chip — fixed-width-ish, left-aligned. */}
      <span
        className="flex-shrink-0"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          height: 20,
          padding: '0 6px',
          background: C.surface2,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          fontFamily: monoFont,
          fontSize: 9,
          letterSpacing: 0.8,
          color: C.muted,
          whiteSpace: 'nowrap',
        }}
      >
        {idText}
      </span>
      {/* Name + vendor, with the description tucked into a `?` tooltip
          to its right. One line each row. */}
      <div className="flex-1 min-w-0 flex items-center gap-1.5">
        <span
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
          title={p.long_name} /* title-ok: shows the full long_name when the row's one-line layout truncates it with ellipsis */
        >
          {p.long_name}
          <span style={{ color: C.muted, fontWeight: 400 }}> · {p.vendor}</span>
        </span>
        <Tooltip
          content={p.description}
          side="top"
          aria-label={`About ${p.long_name}`}
        >
          <span
            aria-hidden
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              color: C.muted,
            }}
          >
            <HelpCircle size={13} strokeWidth={1.5} />
          </span>
        </Tooltip>
      </div>
      {/* Status chip — right-aligned on desktop, wraps to its own line
          on narrow mobile widths via `basis-full sm:basis-auto` so a
          long bound stays on one line. */}
      <div
        className="basis-full sm:basis-auto sm:ml-auto flex-shrink-0 sm:text-right"
      >
        <ProtocolStatusChip tone={protocolTone(p.status)}>
          {protocolStatusText(p)}
        </ProtocolStatusChip>
      </div>
      {/* Visually-hidden description — kept so test harnesses and
          external search can find the registry copy without opening the
          tooltip. */}
      <span
        id={`protocol-desc-${p.id}`}
        data-testid={`protocol-desc-${p.id}`}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {p.description}
      </span>
    </div>
  );
}
